'use client';
import * as React from 'react';
import clsx from 'clsx';
import { SemanticIcon } from '../icons/SemanticIcon.tsx';
import { FEEDBACK_VARIANT_COLORS, FEEDBACK_VARIANT_TINTS, TEXT_SM_SECONDARY_RELAXED, TEXT_SM_SEMIBOLD } from '../common/layout-styles.ts';
import { spacing } from '../css-refs.spacing.ts';
import { borderRadius } from '../css-refs.effects.ts';
import { contentMaxWidth, divider } from '../css-refs.layout.ts';
import { FEEDBACK_VARIANT_ICON_NAMES } from './feedback-variant-icons.ts';
import { feedbackAnnouncementOf } from './feedback-announcement.ts';
import styles from './Alert.module.css';
import type { FeedbackVariant } from '@federa/types/ui-feedback-variant';
import type { AlertProps } from '@federa/types/ui-alert';

/** Resolved variant style (derived from SSOT maps). */
interface VariantStyle {
	bg: string;
	border: string;
	text: string;
}

function getVariantStyle(variant: FeedbackVariant): VariantStyle {
	return {
		bg: FEEDBACK_VARIANT_TINTS[variant],
		border: FEEDBACK_VARIANT_COLORS[variant],
		text: FEEDBACK_VARIANT_COLORS[variant],
	};
}

function getIconContainerStyles(variantText: string): React.CSSProperties {
	return {
		flexShrink: 0,
		marginTop: spacing[0.5],
		color: variantText,
	};
}
/**
 * THE ALERT'S WORDS WRAP AT THE PROSE MEASURE, like every other sentence the system sets.
 *
 * `flex: 1` alone let the body take whatever the container gave it, so an alert in a wide column set as ONE line at
 * the column's width: on the mission board's daemon tab the degraded-read sentence is 122 characters and rendered at
 * roughly 850px, about 1.9x the 448px measure the same page caps its plain prose at. A reader's eye loses the line
 * return at that length, which is the whole reason the token exists -- and an ALERT is the last sentence on a page
 * that should be hard to read.
 *
 * CURED AT THE PRODUCER because `className` lands on the container rather than on this div, so no consumer can spell
 * it locally, and because the class is every alert in the system rather than the one that was noticed. `maxWidth`
 * only ever caps: an alert in a narrow panel is unaffected.
 */
const CONTENT_STYLES: React.CSSProperties = { flex: 1, maxWidth: contentMaxWidth.prose };

function getContainerStyles(variantStyle: VariantStyle): React.CSSProperties {
	return {
		display: 'flex',
		gap: spacing.sm,
		padding: spacing.md,
		backgroundColor: variantStyle.bg,
		// UNIFORM ON ALL FOUR SIDES. The left edge used to be thickened to `borderWidth[4]`, which made the corner radius
		// read differently on the left than on the right and gave the same component two silhouettes depending on which
		// side you looked at. The variant is already carried by the border COLOUR and the icon, so the extra weight was
		// signalling nothing the surface did not already say (operator directive 2026-08-27).
		border: `${divider.thickness.thin} solid ${variantStyle.border}`,
		borderRadius: borderRadius.lg,
	};
}

/**
 * THE TITLE ROW'S STYLE. `marginBottom` is UNCONDITIONAL, and the boolean parameter it used to branch on was dead:
 * the sole call site sits inside the `{title && ...}` guard and passed `!!title`, which is `true` by construction
 * there, so `marginBottom: 0` was unreachable.
 *
 * NEITHER WAS `hasChildren` THE INTENT THE DEAD ARM WAS WAITING FOR -- margin only when a body follows. `children` is
 * a REQUIRED prop on `AlertProps` and the description `<div>` renders UNCONDITIONALLY below, so a body always follows
 * and the spacing is always wanted. Re-deriving the flag from `children` would have changed nothing in any well-typed
 * usage while reading as a fix; the honest change is to delete the parameter and keep the rendering byte-identical.
 */
function getTitleStyles(variantStyle: VariantStyle): React.CSSProperties {
	return {
		...TEXT_SM_SEMIBOLD,
		color: variantStyle.text,
		marginBottom: spacing.xs,
	};
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
	({ variant = 'info', title, children, icon: customIcon, className }, ref) => {
		const variantStyle = getVariantStyle(variant);
		const iconName = FEEDBACK_VARIANT_ICON_NAMES[variant];
		// HOW THIS ONE IS HEARD is the variant's, not this component's (`feedback-announcement.ts`): an error and a
		// warning interrupt, a success and an info wait, and the role and the politeness are taken as one pair so the
		// element cannot say two things about itself.
		const announcement = feedbackAnnouncementOf(variant);

		return (
			<div
				ref={ref}
				role={announcement.role}
				aria-live={announcement.live}
				className={clsx(styles.container, className)}
				style={getContainerStyles(variantStyle)}
			>
				{/* THREE-WAY SLOT (`types/ui-alert.ts`): `undefined` is the variant's own icon, a node replaces it, and `null` is
				    NO icon -- and no cell for one, or the body would stay indented where the icon was. The `??` this replaced
				    read null as "use the default", so a banner had no way to ask for none (operator 2026-09-10, the login
				    modal's code-sent banner). */}
				{customIcon === null ? null : (
					<div style={getIconContainerStyles(variantStyle.text)}>
						{customIcon ?? <SemanticIcon name={iconName} size="sm" color="inherit" />}
					</div>
				)}
				<div style={CONTENT_STYLES}>
					{title && <div style={getTitleStyles(variantStyle)}>{title}</div>}
					<div style={TEXT_SM_SECONDARY_RELAXED}>{children}</div>
				</div>
			</div>
		);
	},
);

Alert.displayName = 'Alert';
